import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
  Slide,
  Zoom,
  Fade,
  Paper,
  Box,
} from "@mui/material";
import {
  Close,
  NavigateBefore,
  NavigateNext,
  Check,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Configure PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TermsAndCatalogDialog = ({ show, handleClose, catalog }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [accepted, setAccepted] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [ipAddress, setIpAddress] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (show) {
      setPageNumber(1);
      setAccepted(false);
      setPdfError(false);
      setPdfLoading(true);

      // Fetch user's IP address
      fetch("https://api.ipify.org?format=json")
        .then((response) => response.json())
        .then((data) => setIpAddress(data.ip))
        .catch(() => setIpAddress("Unknown"));
    }
  }, [show, catalog]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const onDocumentLoadError = () => {
    setPdfError(true);
    setPdfLoading(false);
  };

  const goToPrevPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToNextPage = () =>
    pageNumber < numPages && setPageNumber(pageNumber + 1);

  const handleAcceptanceChange = (event) => {
    setAccepted(event.target.checked);
  };

  const handleSubmit = () => {
    if (accepted) {
      handleClose(accepted, ipAddress); // Pass the acceptance status and IP address to the parent component
    }
  };

  return (
    <Dialog
      open={show}
      //   onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          minHeight: "70vh",
          maxHeight: "90vh",
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          py: 2,
          px: 3,
        }}
      >
        <Typography variant="h6" component="div">
          Terms & Conditions and Catalog
        </Typography>
        {/* <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          sx={{
            transition: "transform 0.2s",
            "&:hover": {
              transform: "rotate(90deg)",
            },
          }}
        >
          <Close />
        </IconButton> */}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, position: "relative" }}>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* PDF Viewer Section */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
              backgroundColor: "#f5f5f5",
              position: "relative",
              overflow: "auto",
              p: 2,
            }}
          >
            {pdfLoading && !pdfError && (
              <Fade in={pdfLoading} style={{ transitionDelay: "200ms" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress color="primary" />
                  <Typography variant="body1" color="text.secondary">
                    Loading document...
                  </Typography>
                </Box>
              </Fade>
            )}

            {pdfError && (
              <Zoom in={pdfError}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    p: 4,
                  }}
                >
                  <Typography
                    variant="h6"
                    color="error"
                    sx={{ fontWeight: "bold" }}
                  >
                    Document Load Error
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Failed to load the document. Please try again later.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setPdfLoading(true);
                      setPdfError(false);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Retry
                  </Button>
                </Box>
              </Zoom>
            )}

            {!pdfError && catalog && (
              <Document
                file={catalog}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress color="primary" />
                    <Typography variant="body1" color="text.secondary">
                      Loading document...
                    </Typography>
                  </Box>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  width={Math.min(800, window.innerWidth - 100)}
                  renderTextLayer={false}
                  loading={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <CircularProgress color="primary" />
                      <Typography variant="body1" color="text.secondary">
                        Rendering page...
                      </Typography>
                    </Box>
                  }
                />
              </Document>
            )}
          </Box>

          {/* PDF Controls */}
          {numPages && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Button
                startIcon={<NavigateBefore />}
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                variant="outlined"
                sx={{
                  minWidth: 120,
                  mr: 2,
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateX(-2px)",
                  },
                }}
              >
                Previous
              </Button>
              <Typography variant="body1" sx={{ mx: 2 }}>
                Page <strong>{pageNumber}</strong> of{" "}
                <strong>{numPages}</strong>
              </Typography>
              <Button
                endIcon={<NavigateNext />}
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                variant="outlined"
                sx={{
                  minWidth: 120,
                  ml: 2,
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateX(2px)",
                  },
                }}
              >
                Next
              </Button>
            </Box>
          )}

          {/* Terms Acceptance */}
          <Paper
            elevation={0}
            sx={{
              m: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: "action.hover",
              transition: "all 0.3s",
              "&:hover": {
                backgroundColor: "action.selected",
                transform: "translateY(-2px)",
              },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={handleAcceptanceChange}
                  color="primary"
                  icon={
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: "white",
                        border: "1px solid gray",
                      }}
                    />
                  }
                  checkedIcon={
                    <Zoom in={true}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                          borderRadius: "4px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Check sx={{ fontSize: 16 }} />
                      </Box>
                    </Zoom>
                  }
                  sx={{
                    p: 0,
                    mr: 1,
                    transition: "all 0.2s",
                  }}
                />
              }
              label={
                <Typography variant="body1">
                  I acknowledge that I have read and agree to the Terms &
                  Conditions and Catalog specifications presented above.
                </Typography>
              }
              sx={{
                m: 0,
                alignItems: "flex-start",
                "& .MuiFormControlLabel-label": {
                  mt: "2px",
                },
              }}
            />
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Your IP:{" "}
          <span
            style={{
              fontWeight: "bolder",
              fontSize: "0.9rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontFamily: "monospace",
              padding: "2px 4px",
              color: "red",
            }}
          >
            {ipAddress}
          </span>
        </Typography>
        <Box>
          <Button
            onClick={() => {
              navigate(-1); // Go back to the previous page
            }}
            variant="outlined"
            sx={{
              mr: 2,
              px: 3,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Go back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!accepted}
            variant="contained"
            color="primary"
            sx={{
              px: 4,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
              },
              "&.Mui-disabled": {
                backgroundColor: "action.disabledBackground",
                color: "action.disabled",
              },
            }}
            endIcon={
              <Zoom in={accepted}>
                <Check sx={{ fontSize: 20 }} />
              </Zoom>
            }
          >
            Accept & Continue
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAndCatalogDialog;
